import { Injectable, signal, computed, inject, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { normalizeBmcPersona } from './ivr-profile-card/ivr-profile-card.component';
import { APP_CONFIG, AppConfig } from 'src/app.config';
export type CallerRole =
    | 'Insured'
    | 'Additional Insured'
    | 'Additional Interest'
    | 'Mortgage'
    | 'Mortgage Services'
    | 'Producer'
    | 'Others'
    | '';

export interface CallerProfile {
    role: CallerRole;
    policyNumber?: string;
    fullName?: string;
    email?: string;
    propertyAddress?: string;
    producerCode?: string;
    producerName?: string;
    partnerCode?: string;
    partnerName?: string;
    mortgageCompanyName?: string;
    companyName?: string;
    description?: string;
    isUnknown?: boolean;
}

export interface BmcUser {
    user_email: string;
    user_firstname: string;
    user_lastname: string;
    user_type: string;
}

export interface BmcPolicy {
    policynumber: string;
    term: number;
    start_date: string;
    end_date: string;
    risk_state: string;
    risk_address1: string;
    risk_city: string;
    risk_county: string;
    risk_zipcode: string;
}

export interface BmcProducer {
    producercode: string;
    producer_fullname: string;
    producer_firstname: string;
    producer_lastname: string;
    partnercode?: string;
    partnername?: string;
}

export interface BmcContact {
    doc_id: string;
    contact: string;
    contacttype: string;
    persona: string;
    source: string;
    systemrecord: string;
    users: BmcUser[];
    policies: BmcPolicy[];
    producer?: BmcProducer;
    organization?: string;
}

export interface BmcApiResponse {
    contacts: BmcContact[];
}

export interface BmcDetailRow {
    label: string;
    value: string;
    key: string;
}

export interface BmcSection {
    title: string;
    baseTitle: string;
    roleImage: string;
    details: BmcDetailRow[];
}

const ROLE_IMAGES: Record<string, string> = {
    Insured: 'insured-icon',
    'Additional Insured': 'insured-icon',
    'AdditionalInsured': 'insured-icon',
    'Additional Interest': 'additional-interest-icon',
    Mortgage: 'mortgage-icon',
    'Mortgage Services': 'mortgage-icon',
    Producer: 'producer-icon',
    Others: 'others-icon',
    'Co-Applicant': 'insured-icon'
};

// Maps backend persona keys to human-readable display names (adds spacing for PascalCase personas)
const PERSONA_DISPLAY_NAMES: Record<string, string> = {
    AdditionalInsured: 'Additional Insured',
    AdditionalInterest: 'Additional Interest',
};

function getPersonaDisplayName(persona: string): string {
    return PERSONA_DISPLAY_NAMES[persona] ?? persona;
}

// Deduplicates contacts that differ only by policy term — keeps the latest term per unique profile
function deduplicateContacts(contacts: BmcContact[]): BmcContact[] {
    const map = new Map<string, BmcContact>();
    for (const c of contacts) {
        const policyNumber = c.policies?.[0]?.policynumber ?? '';

        const key = `${c.persona}::${c.contact}::${policyNumber}`;
        const existing = map.get(key);
        const currentMaxTerm = Math.max(...(c.policies ?? []).map((p) => p.term ?? 0), 0);
        const existingMaxTerm = existing ? Math.max(...(existing.policies ?? []).map((p) => p.term ?? 0), 0) : -1;
        if (!existing || currentMaxTerm > existingMaxTerm) {
            map.set(key, c);
        }
    }
    return [...map.values()];
}

function findUserByType(users: BmcUser[] | undefined, type: string): BmcUser | undefined {
    return users?.find((u) => u.user_type === type);
}

export function mapContactToDetails(contact: BmcContact): BmcDetailRow[] {
    const persona = contact.persona;

    const insuredUser = findUserByType(contact.users, 'Insured');
    const coApplicantUser = findUserByType(contact.users, 'Co-Applicant');
    const policy = contact.policies?.[0];

    const fullName = insuredUser ? `${insuredUser.user_firstname} ${insuredUser.user_lastname}`.trim() : '—';
    const coApplicantFullName = coApplicantUser
        ? `${coApplicantUser.user_firstname} ${coApplicantUser.user_lastname}`.trim()
        : '—';

    const policyAddress = policy
        ? `${policy.risk_address1}, ${policy.risk_city}, ${policy.risk_state} ${policy.risk_zipcode}`
        : '—';

    const additionalInterest = contact.users?.[0];
    const additionalInsured = contact.users?.[0];
    const producer = contact.producer;
    const additionalInterestfullName = additionalInterest
        ? `${additionalInterest.user_firstname} ${additionalInterest.user_lastname}`.trim()
        : '—';
    const additionalInsuredfullName = additionalInsured
        ? `${additionalInsured.user_firstname} ${additionalInsured.user_lastname}`.trim()
        : '—';

    switch (persona) {
        case 'Insured':
            return [
                { label: 'Policy Number', value: policy?.policynumber ?? '—', key: 'policyNumber' },
                { label: 'Full Name', value: fullName, key: 'fullName' },
            ];

        case 'Co-Applicant':
            return [
                { label: 'Policy Number', value: policy?.policynumber ?? '—', key: 'policyNumber' },
                { label: 'Full Name', value: coApplicantFullName, key: 'fullName' },
            ];
        case 'Producer':
            return [
                { label: 'Producer Code', value: producer?.producercode ?? '—', key: 'producerCode' },
                { label: 'Producer Name', value: producer?.producer_fullname ?? '—', key: 'producerName' },
                { label: 'Partner Code', value: producer?.partnercode ?? '—', key: 'partnerCode' },
                { label: 'Partner Name', value: producer?.partnername ?? '—', key: 'partnerName' },
            ];
        case 'Mortgage':
        case 'Mortgage Services':
            return [{ label: 'Mortgage Company', value: contact.organization ?? '—', key: 'mortgageCompanyName' }];
        case 'AdditionalInsured':
            return [
                { label: 'Policy Number', value: policy?.policynumber ?? '—', key: 'policyNumber' },
                { label: 'Full Name', value: additionalInsuredfullName, key: 'fullName' },
                { label: 'Email', value: additionalInsured?.user_email ?? '—', key: 'email' },
                { label: 'Property Address', value: policyAddress, key: 'propertyAddress' },
            ];
        case 'AdditionalInterest':
            return [
                { label: 'Policy Number', value: policy?.policynumber ?? '—', key: 'policyNumber' },
                { label: 'Full Name', value: additionalInterestfullName, key: 'fullName' },
                { label: 'Email', value: additionalInterest?.user_email ?? '—', key: 'email' },
                { label: 'Property Address', value: policyAddress, key: 'propertyAddress' },
            ];
        default:
            return [{ label: 'Full Name', value: 'Unknown', key: 'fullName' }];
    }
}

@Injectable({ providedIn: 'root' })
export class CallerService {
    private http = inject(HttpClient);

    ivrSelectedRole = signal<CallerRole>('');
    dbCallerProfile = signal<CallerProfile[]>([]);
    bmcContacts = signal<BmcContact[]>([]);

    constructor(@Inject(APP_CONFIG) private config: AppConfig) {}

    profilesMatch = computed(() => {
        const ivr = this.ivrSelectedRole();
        const contacts = this.bmcContacts();
        if (!ivr || !contacts.length) return null;
        return contacts.some((c) => normalizeBmcPersona(c.persona) === ivr);
    });

    bmcSections = computed<BmcSection[]>(() => {
        const contacts = this.bmcContacts();
        if (!contacts.length) {
            return [
                {
                    title: 'OTHERS',
                    baseTitle: 'Others',
                    roleImage: 'others-icon',
                    details: [{ label: 'Full Name', value: 'Unknown', key: 'fullName' }],
                },
            ];
        }
        const grouped = new Map<string, BmcContact[]>();
        for (const c of contacts) {
            const list = grouped.get(c.persona) ?? [];
            list.push(c);
            grouped.set(c.persona, list);
        }
        const PERSONA_ORDER: Record<string, number> = {
            Insured: 0,
            'Co-Applicant': 1,
            Producer: 2,
            AdditionalInsured: 3,
            AdditionalInterest: 4,
            Mortgage: 5,
            'Mortgage Services': 6,
        };
        const sorted = [...grouped.entries()].sort(([a], [b]) => (PERSONA_ORDER[a] ?? 99) - (PERSONA_ORDER[b] ?? 99));
        const sections: BmcSection[] = [];
        for (const [persona, group] of sorted) {
            const useIndex = group.length > 1;
            const displayName = getPersonaDisplayName(persona);
            group.forEach((contact, i) => {
                sections.push({
                    title: useIndex ? `${displayName} ${i + 1}`.toUpperCase() : displayName.toUpperCase(),
                    baseTitle: displayName,
                    roleImage: ROLE_IMAGES[persona] ?? 'others-icon',
                    details: mapContactToDetails(contact),
                });
            });
        }
        return sections;
    });

    mapPersonaToRole(persona: string): CallerRole {
        const userType = (persona || '').toString().toLowerCase();
        if (userType.includes('additional insured') || userType.includes('additionalinsured')) return 'Additional Insured';
        if (userType.includes('additional interest') || userType.includes('additionalinterest')) return 'Additional Interest';
        if (userType.includes('mortgage services') || userType.includes('mortgageservices')) return 'Mortgage Services';
        if (userType.includes('mortgage')) return 'Mortgage';
        if (userType.includes('producer')) return 'Producer';
        if (userType.includes('insured') || userType.includes('consumer')) return 'Insured';
        return 'Others';
    }

    lookupCallerByPhone(phone: string, apiUrl: string, persona: string = 'Insured') {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-api-key': this.config.phoneSearchApiKey,
        });
        return this.http
            .post<BmcApiResponse>(apiUrl, { requestType: 'phoneLookup', payload: { phone } }, { headers })
            .pipe(
                tap((response) => {
                    const filtered = (response?.contacts ?? []).filter((c) => c.systemrecord === 'Yes');
                    this.bmcContacts.set(deduplicateContacts(filtered));
                })
            );
    }
}
